<template>
  <Modal
    v-if="isLocationModalOpen"
    :is-open="isOpen"
    size="lg"
    @close="handleClose"
    :close-on-backdrop="true"
    :show-close-button="true"
    content-class="p-0"
    header-class="p-0 border-0"
    close-button-class="absolute top-4 right-4 !p-2 hover:bg-gray-100 rounded-full transition-colors z-10 !w-10 !h-10 !min-w-0 !aspect-square flex items-center justify-center"
    :close-button-icon-size="24"
    close-button-icon-class="text-gray-400"
  >
    <template #header>
      <div
        class="p-8 pb-6 bg-gradient-to-br from-primary-600 to-primary-700 text-white text-center"
      >
        <div
          class="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <MapPin :size="32" class="text-white" />
        </div>
        <h2 class="text-2xl font-serif font-bold mb-2">Select a Location</h2>
        <p class="text-white/80">Choose the location nearest to you</p>
      </div>
    </template>

    <div class="p-6 space-y-4 overflow-y-auto" style="max-height: 60vh">
      <div
        v-if="preparedStores.length === 0"
        class="text-center py-8 text-gray-500"
      >
        <p>No stores available</p>
      </div>
      <button
        v-for="store in preparedStores"
        :key="store.id"
        @click="handleSelect(store)"
        :class="[
          'w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between group',
          selectedStoreId === store.id
            ? 'border-primary-600 bg-primary-50'
            : 'border-gray-100 hover:border-primary-200 hover:bg-gray-50'
        ]"
      >
        <div class="flex items-start gap-4">
          <div
            :class="[
              'flex p-2 rounded-full',
              selectedStoreId === store.id
                ? 'bg-primary-100 text-primary-600'
                : 'bg-gray-100 text-gray-500'
            ]"
          >
            <MapPin :size="20" />
          </div>
          <div>
            <h3
              :class="[
                'font-bold text-lg',
                selectedStoreId === store.id
                  ? 'text-primary-700'
                  : 'text-gray-900'
              ]"
            >
              {{ store.displayName || store.display_name || store.name }}
            </h3>
            <p
              :class="[
                'text-sm mb-1',
                store.status === 'closed' ? 'text-gray-400' : 'text-gray-500'
              ]"
            >
              {{ store.formattedAddress }}
            </p>
            <div class="space-y-1">
              <div
                :class="[
                  'flex items-center gap-1 text-xs font-medium',
                  store.status === 'open'
                    ? 'text-green-600'
                    : store.status === 'schedule_only'
                    ? 'text-yellow-600'
                    : 'text-red-600'
                ]"
              >
                <Clock :size="14" />
                <span>
                  {{ store.statusLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="selectedStoreId === store.id"
          class="flex p-1 rounded-full bg-primary-600"
        >
          <Check :size="16" class="text-white" />
        </div>
      </button>
    </div>
  </Modal>
  <LocationChangeConfirmationModal
    v-else-if="isLocationChangeConfirmationOpen"
    :is-open="isLocationChangeConfirmationOpen"
    :store="pendingLocationSelection"
    @confirm="handleLocationChangeConfirm"
    @cancel="handleLocationChangeCancel"
  />
</template>

<script>
import { mapGetters } from "vuex";
import { Check, MapPin, Clock } from "lucide-vue";
import { Modal } from "@/components/ui";
import { calculateStoreStatus } from "@/utils/storeStatusHelper";
import { useModalManagement } from "@/composables/useModalManagement";
import { useLocationSelection } from "@/composables/useLocationSelection";
import LocationChangeConfirmationModal from "@/components/Location/LocationChangeConfirmationModal.vue";
import { formatLocationAddress } from "@/utils/formatters";

export default {
  name: "LocationModal",
  components: {
    Modal,
    Check,
    MapPin,
    Clock,
    LocationChangeConfirmationModal
  },
  created() {
    this._modalManagement = useModalManagement(this.$store);
    this._locationSelection = useLocationSelection(this.$store);
  },
  data() {
    return {
      isLocationChangeConfirmationOpen: false,
      pendingLocationSelection: null
    };
  },
  computed: {
    ...mapGetters([
      "getStores",
      "getStore",
      "dining_option",
      "availableDiningOptions",
      "getStoreTimezone",
      "isLocationModalOpen",
      "selectedLocation"
    ]),
    isOpen() {
      return this.isLocationModalOpen;
    },
    stores() {
      return this.getStores || [];
    },
    // Computed property to calculate store statuses once
    computedStoreStatusMap() {
      const statusMap = {};
      const stores = this.stores;
      stores.forEach((store) => {
        statusMap[store.id] = this.calculateStoreStatus(store);
      });
      return statusMap;
    },

    selectedStoreId() {
      // Get current store ID from store state
      return this.getStore?.id || null;
    },
    storeStatusMap() {
      // Return computed status map, updating when stores or currentTime changes
      return this.computedStoreStatusMap;
    },

    /**
     * Prepared stores array with all necessary properties for the view
     * This computed property builds the complete store objects once
     * @returns {Array} Array of store objects with status, hours, address, etc.
     */
    preparedStores() {
      const stores = this.stores;
      const allDiningOptions = this.availableDiningOptions || [];

      const prepared = stores.map((store) => {
        // Calculate all properties once per store
        const timezone = store.timezone || this.getStoreTimezone || "UTC";

        // Calculate status using centralized helper (single source of truth)
        const status = calculateStoreStatus(store, allDiningOptions, timezone);
        const statusLabel = this.getStatusLabel(status);

        // Format address once
        const formattedAddress = formatLocationAddress(store);

        return {
          ...store,
          status,
          statusLabel,
          formattedAddress
        };
      });

      return prepared;
    },
    currentDiningOption() {
      if (!this.dining_option || !this.availableDiningOptions) {
        return null;
      }
      return this.availableDiningOptions.find(
        (option) => option.id === this.dining_option
      );
    }
  },
  methods: {
    /**
     * Calculates the store status for a given store
     * Delegates to centralized helper function (single source of truth)
     * @param {Object} store - Store object
     * @returns {string} 'open' | 'closed' | 'schedule_only'
     */
    calculateStoreStatus(store) {
      const timezone = store.timezone || this.getStoreTimezone || "UTC";
      const allDiningOptions = this.availableDiningOptions || [];
      // Use centralized helper function (single source of truth)
      return calculateStoreStatus(store, allDiningOptions, timezone);
    },

    /**
     * Gets the label for a given status
     * @param {string} status - Store status ('open' | 'closed' | 'schedule_only')
     * @returns {string} Status label
     */
    getStatusLabel(status) {
      switch (status) {
        case "open":
          return "Open Now";
        case "schedule_only":
          return "Schedule Only";
        case "closed":
          return "Closed";
        default:
          return "Open Now";
      }
    },

    async handleSelect(store) {
      const result = await this._locationSelection.selectLocation(store);

      if (result.requiresConfirmation) {
        // Show confirmation modal
        this.pendingLocationSelection = store;
        this.isLocationChangeConfirmationOpen = true;
      }

      this._modalManagement.setLocationModalOpen(false);
    },
    handleClose() {
      this._modalManagement.setLocationModalOpen(false);
    },

    handleLocationChangeCancel() {
      // User cancelled, close modal and reset pending selection
      this.isLocationChangeConfirmationOpen = false;
      this.pendingLocationSelection = null;
    },
    async handleLocationChangeConfirm() {
      if (this.pendingLocationSelection) {
        // Delegate to composable (which delegates to store)
        await this._locationSelection.confirmLocationChange(
          this.pendingLocationSelection
        );

        // Close modals
        this.isLocationChangeConfirmationOpen = false;
        this.pendingLocationSelection = null;
        this._modalManagement.setLocationModalOpen(false);
      }
    }
  }
};
</script>
